import { useState, useMemo } from 'react';
import { IconChevronDown, IconChevronUp } from '@tabler/icons-react';

import Spacer from '../../components/Spacer';
import { rupiah } from '@lib/currency';
import { DataDetailCheckout, SettingsProps } from '../types';

interface SectionSummaryOrderProps {
  data: DataDetailCheckout;
  settings: SettingsProps;
}

const SectionSummaryOrder: React.FC<SectionSummaryOrderProps> = ({
  data,
  settings,
}) => {
  const [isOpen, setIsOpen] = useState(false);

  // Safe data extraction with fallbacks
  const invoiceItems = useMemo(() => {
    return data?.invoice_payment_items || [];
  }, [data?.invoice_payment_items]);

  const totalAmount = useMemo(() => {
    return data?.total ?? 0;
  }, [data?.total]);

  const subtotalAmount = useMemo(() => {
    return data?.subtotal ?? 0;
  }, [data?.subtotal]);

  const taxRate = useMemo(() => {
    return data?.tax ?? 0;
  }, [data?.tax]);

  const taxAmount = useMemo(() => {
    return subtotalAmount * taxRate;
  }, [subtotalAmount, taxRate]);

  // Memoize additional data calculations
  const addtionalData = useMemo(
    () => [
      {
        label: 'Subtotal',
        value: subtotalAmount ? rupiah(subtotalAmount) : '-',
      },
      {
        label: 'Tax',
        value: taxRate
          ? `(${(taxRate * 100).toFixed(0)}%) / ${rupiah(taxAmount)}`
          : '-',
      },
      {
        label: 'Discount',
        value: data?.discount ? rupiah(data.discount) : '-',
      },
      {
        label: 'Fee',
        value: data?.fees ? rupiah(data.fees) : '-',
      },
    ],
    [subtotalAmount, taxRate, taxAmount, data?.discount, data?.fees]
  );

  const sidebarTextColor = settings?.sidebar_text_color || '#5D87FF';
  const buttonColor = settings?.button_color || '#5D87FF';

  return (
    <>
      <div className="mb-4 block w-full rounded-xl border border-gray-300 bg-white p-4 shadow-sm lg:hidden">
        <div
          className="cursor-pointer select-none"
          onClick={() => setIsOpen(!isOpen)}
        >
          <div className="flex flex-col gap-4">
            <div className="flex flex-row items-center justify-between">
              <span className="text-base font-semibold text-black">
                Order Summary
              </span>
              {isOpen ? (
                <IconChevronUp className="h-5 w-5 text-gray-600" />
              ) : (
                <IconChevronDown className="h-5 w-5 text-gray-600" />
              )}
            </div>

            {isOpen && (
              <div className="bg-white py-4">
                <div className="space-y-4">
                  {/* Order Items */}
                  {invoiceItems.length > 0 ? (
                    <div className="space-y-2">
                      {invoiceItems.map((item, index) => (
                        <div
                          key={index}
                          className="grid grid-cols-3 text-sm text-gray-600"
                        >
                          <span className="font-normal">
                            {item?.item_name || 'Item'}
                          </span>
                          <span className="text-center font-normal">
                            x{item?.quantity || 0}
                          </span>
                          <span className="text-right font-medium text-black">
                            {rupiah((item?.quantity || 0) * (item?.price || 0))}
                          </span>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="py-2 text-center text-sm text-gray-500">
                      No items
                    </div>
                  )}

                  <hr className="border-gray-200" />

                  {/* Additional Data */}
                  <div className="space-y-2">
                    {addtionalData.map((item, idx) => (
                      <div key={idx} className="grid grid-cols-2 text-sm">
                        <span className="text-left font-normal text-gray-600">
                          {item.label}
                        </span>
                        <span className="text-right font-medium text-black">
                          {item.value}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            )}

            <hr className="border-gray-200" />

            <div className="flex items-center justify-between">
              <span className="text-base font-semibold text-gray-900">
                Total
              </span>
              <span
                className="text-lg font-bold"
                style={{ color: buttonColor }}
              >
                {rupiah(totalAmount)}
              </span>
            </div>
          </div>
        </div>
      </div>

      {/* Desktop Summary */}
      <div className="hidden h-full w-full flex-col items-center justify-center p-4 lg:flex">
        <div className="flex h-fit w-full flex-col gap-4 rounded-lg p-6">
          <h1 className="mb-8 text-2xl font-bold">Order Summary</h1>

          {/* Order Items */}
          {invoiceItems.length > 0 ? (
            <div className="space-y-2">
              {invoiceItems.map((item, index) => (
                <div key={index} className="grid grid-cols-3 text-gray-600">
                  <small className="text-base font-light leading-none">
                    {item?.item_name || 'Item'}
                  </small>
                  <small className="text-center text-base font-light leading-none">
                    x{item?.quantity || 0}
                  </small>
                  <small className="text-right text-base font-light leading-none">
                    {rupiah((item?.quantity || 0) * (item?.price || 0))}
                  </small>
                </div>
              ))}
            </div>
          ) : (
            <div className="py-2 text-center text-base text-gray-500">
              No items
            </div>
          )}

          <Spacer />

          <div className="space-y-2">
            {addtionalData.map((item, idx) => {
              return (
                <div
                  key={idx}
                  className="flex items-center justify-between text-base"
                >
                  <div className="flex flex-1 flex-row items-center justify-between font-normal text-neutral-600">
                    <span>{item.label}</span>
                  </div>
                  <span className="ml-4 font-medium text-black">
                    {item.value}
                  </span>
                </div>
              );
            })}
          </div>

          <Spacer />

          <div className="flex items-center justify-between">
            <span className="text-lg font-semibold">Total</span>
            <span
              className="text-2xl font-semibold"
              style={{ color: sidebarTextColor }}
            >
              {rupiah(totalAmount)}
            </span>
          </div>
        </div>
      </div>
    </>
  );
};

export default SectionSummaryOrder;
